import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatTL, formatDateTR, todayISO } from "@/lib/format";
import { PageHeader, AddButton, Field, inputCls, StatusPill } from "@/components/shared";
import { usePermission } from "@/lib/permissions";
import FilterBar from "@/components/FilterBar";
import { downloadCSV, fmtDate, fmtNum } from "@/lib/csv";
import { Trash2, Pencil, X } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  pending: { label: "Bekliyor", cls: "bg-[#D4A373]/20 text-[#8A5A44]" },
  collected: { label: "Tahsil edildi", cls: "bg-[#4A6741]/15 text-[#4A6741]" },
  bounced: { label: "Karşılıksız", cls: "bg-[#B24C4C]/15 text-[#B24C4C]" },
};

const emptyForm = {
  cheque_no: "",
  amount: "",
  due_date: todayISO(),
  bank: "",
  status: "pending",
  direction: "incoming",
};

export default function Cheques() {
  const canEdit = usePermission("cheques", "edit");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/cheques");
    setItems(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((c) => {
      if (q && !`${c.cheque_no} ${c.bank}`.toLowerCase().includes(q)) return false;
      if (status && c.status !== status) return false;
      if (dateFrom && c.due_date < dateFrom) return false;
      if (dateTo && c.due_date > dateTo) return false;
      return true;
    });
  }, [items, search, status, dateFrom, dateTo]);

  const exportCSV = () => {
    downloadCSV(filtered, [
      { label: "Çek No", key: "cheque_no" },
      { label: "Banka", key: "bank" },
      { label: "Tür", get: (r) => (r.direction === "incoming" ? "Gelen" : "Verilen") },
      { label: "Vade", get: (r) => fmtDate(r.due_date) },
      { label: "Tutar (TL)", get: (r) => fmtNum(r.amount) },
      { label: "Durum", get: (r) => STATUS[r.status]?.label || r.status },
    ], `cekler_${todayISO()}.csv`);
  };

  const clearFilter = () => { setSearch(""); setStatus(""); setDateFrom(""); setDateTo(""); };

  const openAdd = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...c, amount: String(c.amount) });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, amount: parseFloat(form.amount) || 0 };
    try {
      if (editing) {
        await api.put(`/cheques/${editing.id}`, payload);
        toast.success("Çek güncellendi");
      } else {
        await api.post("/cheques", payload);
        toast.success("Çek eklendi");
      }
      setOpen(false); load();
    } catch (e) {
      toast.error("Kayıt başarısız");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu çeki silmek istediğinize emin misiniz?")) return;
    await api.delete(`/cheques/${id}`);
    toast.success("Çek silindi");
    load();
  };

  return (
    <div className="p-6 md:p-10 lg:p-12 fade-in max-w-[1400px]">
      <PageHeader
        eyebrow="Ödeme araçları"
        title="Çekler"
        description="Gelen ve verilen çeklerinizi tek ekrandan takip edin."
        action={canEdit ? <AddButton onClick={openAdd} label="Yeni Çek" testid="add-cheque-btn" /> : null}
      />

      <FilterBar
        search={search} onSearch={setSearch}
        status={status} onStatus={setStatus}
        statusOptions={[
          { value: "pending", label: "Bekliyor" },
          { value: "collected", label: "Tahsil edildi" },
          { value: "bounced", label: "Karşılıksız" },
        ]}
        dateFrom={dateFrom} onDateFrom={setDateFrom}
        dateTo={dateTo} onDateTo={setDateTo}
        onExport={exportCSV}
        onClear={clearFilter}
        searchPlaceholder="Çek no veya banka…"
        testidPrefix="cheques-filter"
      />

      <div className="bg-white border border-[#E6E4DD] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="cheques-table">
            <thead>
              <tr className="text-xs tracking-[0.1em] uppercase font-bold text-[#5C5B57] border-b border-[#E6E4DD]">
                <th className="px-6 py-4">Çek No</th>
                <th className="px-6 py-4">Banka</th>
                <th className="px-6 py-4">Tür</th>
                <th className="px-6 py-4">Vade</th>
                <th className="px-6 py-4">Tutar</th>
                <th className="px-6 py-4">Durum</th>
                <th className="px-6 py-4 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan="7" className="px-6 py-12 text-center text-[#5C5B57] text-sm">Henüz çek eklenmedi.</td></tr>
              )}
              {items.length > 0 && filtered.length === 0 && (
                <tr><td colSpan="7" className="px-6 py-12 text-center text-[#5C5B57] text-sm">Filtreye uyan kayıt bulunamadı.</td></tr>
              )}
              {filtered.map((c) => (
                <tr key={c.id} className="border-b border-[#E6E4DD]/60 hover:bg-[#F0EFEA]/40 transition-colors" data-testid={`cheque-row-${c.id}`}>
                  <td className="px-6 py-4 font-mono text-sm text-[#2D2C2A]">#{c.cheque_no}</td>
                  <td className="px-6 py-4 text-[#2D2C2A]">{c.bank}</td>
                  <td className="px-6 py-4 text-sm">
                    <span className={c.direction === "incoming" ? "text-[#4A6741]" : "text-[#B24C4C]"}>
                      {c.direction === "incoming" ? "Gelen" : "Verilen"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-[#5C5B57]">{formatDateTR(c.due_date)}</td>
                  <td className="px-6 py-4 font-mono font-medium text-[#2D2C2A]">{formatTL(c.amount)}</td>
                  <td className="px-6 py-4"><StatusPill status={c.status} map={STATUS} /></td>
                  <td className="px-6 py-4 text-right">
                    {canEdit && (
                    <div className="inline-flex gap-1">
                      <button onClick={() => openEdit(c)} data-testid={`edit-cheque-${c.id}`} className="p-2 rounded-lg hover:bg-[#F0EFEA] text-[#5C5B57]"><Pencil size={16} /></button>
                      <button onClick={() => remove(c.id)} data-testid={`delete-cheque-${c.id}`} className="p-2 rounded-lg hover:bg-[#B24C4C]/10 text-[#B24C4C]"><Trash2 size={16} /></button>
                    </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 fade-in" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl p-8 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="cheque-form-modal">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-2xl text-[#2D2C2A]">{editing ? "Çek Düzenle" : "Yeni Çek"}</h3>
              <button onClick={() => setOpen(false)} className="text-[#5C5B57] hover:text-[#2D2C2A]"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Çek No"><input required className={inputCls} data-testid="cheque-no-input" value={form.cheque_no} onChange={(e) => setForm({ ...form, cheque_no: e.target.value })} /></Field>
                <Field label="Banka"><input required className={inputCls} data-testid="cheque-bank-input" value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} /></Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Tutar (₺)"><input required type="number" step="0.01" min="0" className={inputCls} data-testid="cheque-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Field>
                <Field label="Vade Tarihi"><input required type="date" className={inputCls} data-testid="cheque-due-date-input" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Tür">
                  <select className={inputCls} data-testid="cheque-direction-select" value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })}>
                    <option value="incoming">Gelen (Alacak)</option>
                    <option value="outgoing">Verilen (Borç)</option>
                  </select>
                </Field>
                <Field label="Durum">
                  <select className={inputCls} data-testid="cheque-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    <option value="pending">Bekliyor</option>
                    <option value="collected">Tahsil edildi</option>
                    <option value="bounced">Karşılıksız</option>
                  </select>
                </Field>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="flex-1 border border-[#E6E4DD] rounded-xl px-6 py-3 text-[#2D2C2A] hover:bg-[#F0EFEA]">İptal</button>
                <button type="submit" data-testid="cheque-submit" className="flex-1 bg-[#8A5A44] text-white hover:bg-[#714835] rounded-xl px-6 py-3 font-medium">{editing ? "Güncelle" : "Ekle"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
